import React, { useState } from 'react';
import { loginMachine, useLoginStore } from './auth/loginMachine';
import './SigninPopup.css';

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.22c-.5-1.52-.76-3.13-.76-4.72s.26-3.2.76-4.72l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.56z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const EmailIcon = ({ className }) => (
  <i className={`fas fa-envelope ${className || ''}`} aria-hidden="true" />
);

export default function SigninPopup({ onClose, isAnonymous = false, error = '', compact = false, cardOverlay = false }) {
  const ui = useLoginStore((state) => state.ui);
  const lastError = useLoginStore((state) => state.lastError);
  const [mode, setMode] = useState('providers');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isEmailSending, setIsEmailSending] = useState(false);
  const isGoogleLoading = ui.kind === 'LoginBusy' && ui.method !== 'email-link';
  const reason = ui.kind === 'Idle' ? 'manual' : ui.reason;
  const visibleError = emailError || error || lastError || '';
  const headingText = isAnonymous ? 'Sign in and get more free audio guides' : 'Sign in';

  const handleGoogleLogin = async () => {
    try {
      await loginMachine.continueWithGooglePopup(reason);
    } catch (err) {
      console.error('[SigninPopup] Google sign-in failed:', err);
    }
  };

  const handleSendEmailLink = async () => {
    setEmailError('');
    setIsEmailSending(true);

    try {
      await loginMachine.sendEmailSignInLink(email);
      setMode('email-sent');
    } catch (err) {
      setEmailError(getEmailAuthErrorMessage(err));
    } finally {
      setIsEmailSending(false);
    }
  };

  const getEmailAuthErrorMessage = (err) => {
    const code = err?.code;
    switch (code) {
      case 'auth/invalid-email':
        return 'Invalid email address. Please check and try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/quota-exceeded':
        return 'Email quota exceeded. Please try again later.';
      default:
        return err?.message || 'Failed to send email. Please try again.';
    }
  };

  const backdropClassName = cardOverlay
    ? 'sip-card-area-backdrop'
    : compact
      ? 'sip-backdrop sip-backdrop-compact'
      : 'sip-backdrop';
  const cardClassName = cardOverlay
    ? 'sip-card sip-card-compact sip-card-area-modal'
    : compact
      ? 'sip-card sip-card-compact'
      : 'sip-card';

  return (
    <div className={backdropClassName} onClick={cardOverlay ? onClose : undefined}>
      <div className={cardClassName} onClick={cardOverlay ? (event) => event.stopPropagation() : undefined}>
        <button className="sip-close" onClick={onClose} aria-label="Close sign-in dialog">×</button>

        <h2 className={isAnonymous ? 'sip-anonymous-heading' : ''}>
          {isAnonymous ? <span className="sip-free-audio-text">{headingText}</span> : headingText}
        </h2>

        {visibleError && <div className="sip-error">{visibleError}</div>}

        {mode === 'providers' && (
          <div className="sip-action-stack">
            <button
              type="button"
              className="sip-google-action"
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading || isEmailSending}
            >
              <GoogleIcon className="w-5 h-5" />
              <span>{isGoogleLoading ? 'Signing in...' : 'Continue with Google'}</span>
            </button>

            <button
              type="button"
              className="sip-email-action"
              onClick={() => {
                setEmailError('');
                setMode('email');
              }}
              disabled={isGoogleLoading || isEmailSending}
            >
              <EmailIcon />
              <span>Continue with Email</span>
            </button>
          </div>
        )}

        {mode === 'email' && (
          <>
            <button
              className="sip-back-btn"
              onClick={() => {
                setEmailError('');
                setMode('providers');
              }}
              aria-label="Back to sign-in options"
            >
              <i className="fas fa-arrow-left" aria-hidden="true" />
            </button>

            <div className="sip-email-input-container">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="sip-email-input"
                autoComplete="email"
              />
            </div>

            <button
              type="button"
              className="sip-send-email-btn"
              onClick={handleSendEmailLink}
              disabled={isEmailSending || !email}
            >
              {isEmailSending ? 'Sending...' : 'Send Sign-in Link'}
            </button>
          </>
        )}

        {mode === 'email-sent' && (
          <>
            <button
              className="sip-back-btn"
              onClick={() => {
                setEmailError('');
                setMode('providers');
              }}
              aria-label="Back to sign-in options"
            >
              <i className="fas fa-arrow-left" aria-hidden="true" />
            </button>
            <p className="sip-email-instructions">
              Check {email || 'your email'} for a sign-in link. Also check your spam folder.
            </p>
            <button
              type="button"
              className="sip-resend-btn"
              onClick={handleSendEmailLink}
              disabled={isEmailSending}
            >
              {isEmailSending ? 'Sending...' : 'Resend Email'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
